/* =====================================================================================
   Turning a Postgres refusal into something a person can act on.

   Row Level Security refuses a write with "new row violates row-level security policy for
   table X". That is exactly right as a security posture and useless as a message: it tells
   the person nothing about what to do, and it looks identical whether they lack a role, are
   in the wrong organisation, or have hit a genuine bug.

   This cost a real user a working morning. She held the `slt` role, which is a visibility
   overlay that publishes notices and shows the whole diary, and is deliberately not in
   app.can_write(). Every attempt to log an interaction was refused, with no indication that a
   role was the reason.

   The translations below never guess at what the policy allows. They say what class of
   problem it is and who can resolve it, which is the part the person needs.
   ===================================================================================== */

export function explain(message: string, action = 'save this'): string {
  const m = message.toLowerCase();

  if (m.includes('row-level security') || m.includes('row level security')) {
    /* DELIBERATELY NO LONGER NAMES A CAUSE.

       This used to say "writing commercial records needs the Commercial role, and Leadership on
       its own does not include it". Confident, actionable, and in the case that mattered, wrong:
       the user held Commercial already. The write was refused because the client omitted org_id,
       which makes app.can_write(null) false and surfaces a missing column as a policy violation.
       She was sent to ask for access she had, and the real defect went unlooked-for for a day.

       A policy refusal has several possible causes and this layer cannot tell them apart. It now
       says so, and names both candidates, rather than asserting the one that is easiest to
       write. Being unhelpfully vague costs a question; being confidently wrong costs a day. */
    return `The database refused the attempt to ${action}. This is usually one of two things, `
         + `and the message cannot tell them apart: either your roles do not cover it, or the `
         + `form did not send everything the record needs. Check your roles in Crew first. If `
         + `Commercial is already there, it is the second one, which is a defect rather than a `
         + `permission problem, so report it rather than requesting access.`;
  }
  if (m.includes('violates foreign key')) {
    return 'Something this record points at no longer exists. It may have been merged or '
         + 'deleted while this form was open. Reload and try again.';
  }
  if (m.includes('duplicate key') || m.includes('23505')) {
    return 'This already exists. Look for it rather than creating a second copy.';
  }
  if (m.includes('violates check constraint')) {
    // Surface the constraint name: it is the fastest route to the actual rule.
    const c = message.match(/constraint "([^"]+)"/)?.[1];
    return `One of these values is not allowed by the database rule${c ? ` (${c})` : ''}. `
         + 'That rule exists deliberately, so the value needs correcting rather than forcing.';
  }
  if (m.includes('null value in column')) {
    const col = message.match(/column "([^"]+)"/)?.[1];
    return `${col ? `"${col}" is required` : 'A required field is missing'} and was left empty.`;
  }
  if (m.includes('jwt') || m.includes('expired')) {
    return 'Your session has expired. Sign in again; nothing you typed has been sent.';
  }
  if (m.includes('failed to fetch') || m.includes('networkerror')) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return message;
}
